/**
 * FX / đa tiền tệ (US-E18.F3-0196 · S03 · @UC9279–@UC9283) — @gate money 🔴🔴.
 *
 * ⚠️ ADR 0005 (PO CHỐT hướng 1): **QUY ĐỔI Ở TẦNG NHẬP LIỆU, LEDGER GIỮ ĐƠN-CURRENCY.**
 * Khoản thu/chi bằng ngoại tệ được quy đổi về đồng tiền CỦA SỔ **ngay tại thời điểm ghi**; CHỈ số
 * tiền đã quy đổi (đơn-currency) mới đi vào ledger. Tỷ giá gốc + số ngoại tệ gốc lưu như METADATA
 * trên `ledger_transactions` — **KHÔNG phải một dòng ledger loại tiền khác**.
 * ⇒ currency-mirror (CHECK/composite-FK/trigger fund→account→line) **KHÔNG THÁO GÌ CẢ**;
 *   `assert_tx_balanced` (cộng amountMinor không phân biệt currency) vẫn ĐÚNG vì mọi line cùng tiền;
 *   `LedgerService.reverse` vẫn chạy (mọi tx đơn-currency) — assert fail-loud multi-ccy GIỮ NGUYÊN.
 *
 * THUẦN: không DB, không I/O, deterministic.
 *
 * INVARIANTS (B1 no-float 🔴):
 *  - Tiền = BigInt minor units.
 *  - **Tỷ giá KHÔNG phải tiền** nhưng CŨNG KHÔNG dùng float/Number: lưu **BigInt scaled ×10^8**
 *    (`FX_RATE_SCALE`). Chuỗi thập phân "24500.5" → BigInt bằng thao tác CHUỖI (không parseFloat).
 *  - Làm tròn quy đổi: **HALF-UP trên giá trị tuyệt đối** — xác định, đối xứng dấu, thuần BigInt.
 *  - Kết quả quy đổi PHẢI > 0 ⇒ tiền không "bốc hơi" thành 0đ âm thầm (fail-loud).
 *  - Thiếu tỷ giá ⇒ ném `FxRateMissingError` (caller map 422) — **KHÔNG bịa 1:1**.
 *
 * P1.3 (đồng lẻ có tên người gánh) — KHÔNG mâu thuẫn: quy đổi là 1 số → 1 số (không chia), nên không
 * có đồng lẻ để gán. Việc CHIA khoản chi ngoại tệ cho nhiều người diễn ra SAU quy đổi, trên số đã
 * quy đổi, qua `splitEngine`/`distributeWithRemainder` cũ (1 nguồn — B3).
 */

import { DomainError } from "./errors.js";

/* ---------------------------------------------------------------------------
 * Thang tỷ giá (no-float)
 * ------------------------------------------------------------------------- */

/** Số chữ số thập phân của tỷ giá lưu trữ. Tỷ giá thật = rateScaled / 10^FX_RATE_SCALE_EXP. */
export const FX_RATE_SCALE_EXP = 8;

/** Thang nhân tỷ giá: 10^8. `rateScaled` (BigInt) = tỷ giá × FX_RATE_SCALE. KHÔNG float. */
export const FX_RATE_SCALE: bigint = 10n ** BigInt(FX_RATE_SCALE_EXP);

/**
 * Số chữ số thập phân của ĐƠN VỊ TIỀN (minor exponent): VND 0 (1 đồng = minor), USD 2 (1$ = 100
 * cents). Cần khi quy đổi giữa 2 tiền khác bậc minor. Nguồn: ISO 4217.
 */
export const CURRENCY_MINOR_EXPONENTS: Readonly<Record<string, number>> = {
  VND: 0,
  JPY: 0,
  KRW: 0,
  USD: 2,
  EUR: 2,
  GBP: 2,
  AUD: 2,
  SGD: 2,
  THB: 2,
  CNY: 2,
  CAD: 2,
  CHF: 2,
  HKD: 2,
  MYR: 2,
  TWD: 2,
};

/** Tiền không nằm trong bảng ⇒ 2 chữ số (phổ biến nhất). An toàn: chỉ ảnh hưởng bậc, không float. */
export const DEFAULT_MINOR_EXPONENT = 2;

/** Số chữ số thập phân của 1 đơn vị tiền (case-insensitive, trim). */
export function minorExponentOf(currency: string): number {
  const key = currency.trim().toUpperCase();
  return CURRENCY_MINOR_EXPONENTS[key] ?? DEFAULT_MINOR_EXPONENT;
}

/* ---------------------------------------------------------------------------
 * Lỗi FX (caller map HTTP)
 * ------------------------------------------------------------------------- */

/** Số tiền ngoại tệ ≤ 0, hoặc quy đổi ra ≤ 0 (tiền "bốc hơi") ⇒ fail-loud. Caller map 422. */
export class FxAmountError extends Error {
  readonly code = "FX_AMOUNT_INVALID";
  constructor(message: string) {
    super(`[fx] ${message}`);
  }
}

/** Tỷ giá ≤ 0 / sai định dạng ⇒ fail-closed (không đoán). Caller map 422. */
export class FxRateInvalidError extends Error {
  readonly code = "FX_RATE_INVALID";
  constructor(readonly raw: string) {
    super(`[fx] tỷ giá không hợp lệ: '${raw}' (phải là số thập phân dương, ≤ ${FX_RATE_SCALE_EXP} chữ số thập phân)`);
  }
}

/** KHÔNG có tỷ giá hiệu lực cho cặp tiền/thời điểm ⇒ **422 fail-closed**, KHÔNG bịa tỷ giá. */
export class FxRateMissingError extends Error {
  readonly code = "FX_RATE_MISSING";
  constructor(
    readonly baseCurrency: string,
    readonly quoteCurrency: string,
    readonly at: Date,
  ) {
    super(
      `[fx] không có tỷ giá ${baseCurrency}→${quoteCurrency} hiệu lực tại ${at.toISOString()} ` +
        `— nhập tỷ giá trước khi ghi khoản ngoại tệ (KHÔNG tự bịa).`,
    );
  }
}

/* ---------------------------------------------------------------------------
 * Parse tỷ giá: chuỗi thập phân → BigInt scaled (KHÔNG parseFloat 🔴)
 * ------------------------------------------------------------------------- */

const DECIMAL_PATTERN = /^(\d+)(?:\.(\d+))?$/;

/**
 * "24500" / "24500.5" / "0.00000001" → BigInt scaled ×10^8. Thao tác CHUỖI thuần (pad số thập phân),
 * KHÔNG `parseFloat`/`Number` (tránh sai số nhị phân trên đường tiền — B1).
 *
 * @throws FxRateInvalidError nếu rỗng/rác/số mũ/âm/0 hoặc > FX_RATE_SCALE_EXP chữ số thập phân
 *         (không tự làm tròn tỷ giá — người dùng phải biết tỷ giá mình nhập).
 */
export function parseFxRateToScaled(raw: string): bigint {
  const trimmed = raw.trim();
  const m = DECIMAL_PATTERN.exec(trimmed);
  if (!m) throw new FxRateInvalidError(raw);

  const intPart = m[1] as string;
  const fracPart = m[2] ?? "";
  if (fracPart.length > FX_RATE_SCALE_EXP) throw new FxRateInvalidError(raw);

  const padded = fracPart.padEnd(FX_RATE_SCALE_EXP, "0");
  const scaled = BigInt(intPart) * FX_RATE_SCALE + BigInt(padded === "" ? "0" : padded);
  if (scaled <= 0n) throw new FxRateInvalidError(raw);
  return scaled;
}

/** BigInt scaled → chuỗi thập phân hiển thị (không mất chữ số, không float). */
export function formatFxRateScaled(rateScaled: bigint): string {
  const whole = rateScaled / FX_RATE_SCALE;
  const frac = (rateScaled % FX_RATE_SCALE).toString().padStart(FX_RATE_SCALE_EXP, "0");
  const trimmedFrac = frac.replace(/0+$/, "");
  return trimmedFrac === "" ? whole.toString() : `${whole.toString()}.${trimmedFrac}`;
}

/* ---------------------------------------------------------------------------
 * Quy đổi (1 NGUỒN công thức — B3)
 * ------------------------------------------------------------------------- */

export interface ConvertToFundCurrencyInput {
  /** Số tiền NGOẠI TỆ (minor units của `fromCurrency`, vd 100 USD = 10_000 cents). */
  readonly originalAmountMinor: bigint;
  readonly fromCurrency: string;
  /** Đồng tiền CỦA SỔ (ledger chỉ nhận số này). */
  readonly toCurrency: string;
  /** Tỷ giá 1 `fromCurrency` = ? `toCurrency`, BigInt scaled ×10^8. */
  readonly rateScaled: bigint;
}

/**
 * Quy đổi số ngoại tệ → minor units của đồng tiền SỔ. THUẦN, no-float, làm tròn HALF-UP xác định.
 *
 * Công thức:
 *   converted = round( amount × rateScaled × 10^(toExp − fromExp) / FX_RATE_SCALE )
 * (toExp/fromExp = số chữ số thập phân đơn vị tiền — vd cent USD exp 2 vs đồng VND exp 0.)
 *
 * Làm tròn HALF-UP trên |giá trị| rồi trả dấu ⇒ xác định + đối xứng (không phụ thuộc dấu, không
 * phụ thuộc thứ tự gọi). Kết quả 0 ⇒ ném `FxAmountError` (tiền không được bốc hơi im lặng).
 */
export function convertToFundCurrency(input: ConvertToFundCurrencyInput): bigint {
  const zero = 0n;
  if (input.originalAmountMinor <= zero) {
    throw new FxAmountError(`số tiền ngoại tệ phải > 0 (nhận ${input.originalAmountMinor})`);
  }
  if (input.rateScaled <= zero) {
    throw new FxRateInvalidError(input.rateScaled.toString());
  }

  const fromExp = minorExponentOf(input.fromCurrency);
  const toExp = minorExponentOf(input.toCurrency);
  const expDiff = toExp - fromExp;

  // numerator / denominator — BigInt thuần, KHÔNG float.
  let numerator = input.originalAmountMinor * input.rateScaled;
  let denominator = FX_RATE_SCALE;
  if (expDiff > 0) {
    numerator *= 10n ** BigInt(expDiff);
  } else if (expDiff < 0) {
    denominator *= 10n ** BigInt(-expDiff);
  }

  const converted = divideRoundHalfUp(numerator, denominator);
  if (converted <= zero) {
    throw new FxAmountError(
      `quy đổi ra ${converted} (≤ 0) — số ngoại tệ quá nhỏ so với tỷ giá; không ghi bút toán 0đ.`,
    );
  }
  return converted;
}

/** Chia BigInt làm tròn HALF-UP trên trị tuyệt đối (thuần, xác định). denominator > 0. */
function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  const zero = 0n;
  const two = 2n;
  const negative = numerator < zero;
  const abs = negative ? -numerator : numerator;
  // floor(abs/den) + (remainder×2 ≥ den ? 1 : 0)  ⇔ HALF-UP.
  const quotient = abs / denominator;
  const remainder = abs % denominator;
  const rounded = remainder * two >= denominator ? quotient + 1n : quotient;
  return negative ? -rounded : rounded;
}

/* ---------------------------------------------------------------------------
 * Config: chính sách chốt tỷ giá cấp nhóm (`fx_rate_policy`)
 * ------------------------------------------------------------------------- */

/**
 * Chính sách chốt tỷ giá:
 *  - `at_spend`      (MẶC ĐỊNH): tỷ giá HIỆU LỰC TẠI THỜI ĐIỂM CHI (`effectiveAt ≤ spentAt`, mới
 *    nhất). Số tiền sổ chốt cứng lúc ghi — về sau tỷ giá đổi KHÔNG hồi tố.
 *  - `at_settlement`: tỷ giá HIỆU LỰC TẠI THỜI ĐIỂM QUYẾT TOÁN (`effectiveAt ≤ now`, mới nhất) —
 *    nhóm quyết toán theo tỷ giá ngày quyết toán.
 */
export const FX_RATE_POLICIES = ["at_spend", "at_settlement"] as const;
export type FxRatePolicy = (typeof FX_RATE_POLICIES)[number];

/** Khoá config FX (registry `system_settings`, override được ở group/fund). */
export const FX_CONFIG_KEYS = ["fx_rate_policy"] as const;
export type FxConfigKey = (typeof FX_CONFIG_KEYS)[number];

/**
 * Default hệ thống — GHI RÕ (không im lặng đoán): `at_spend`. Lý do: tỷ giá lúc chi là điều người
 * dùng CHỨNG KIẾN (hoá đơn/biên lai) ⇒ số sổ khớp thực tế; `at_settlement` là lựa chọn CÓ Ý THỨC.
 */
export const FX_CONFIG_DEFAULTS: { readonly fx_rate_policy: FxRatePolicy } = {
  fx_rate_policy: "at_spend",
};

/** True nếu `value` thuộc whitelist chính sách (type-guard cho resolver fail-safe). */
export function isFxRatePolicy(value: unknown): value is FxRatePolicy {
  return typeof value === "string" && (FX_RATE_POLICIES as readonly string[]).includes(value);
}

/** True nếu `key` là khoá config FX. */
export function isFxConfigKey(key: string): key is FxConfigKey {
  return (FX_CONFIG_KEYS as readonly string[]).includes(key);
}

/** Validate giá trị MỚI của khoá config FX TRƯỚC khi ghi. Fail-closed key lạ / value ngoài whitelist. */
export function validateFxConfig(key: FxConfigKey, value: unknown): void {
  switch (key) {
    case "fx_rate_policy": {
      if (!isFxRatePolicy(value)) throw new DomainError("FX_CONFIG_INVALID");
      return;
    }
    default:
      throw new DomainError("FX_CONFIG_INVALID");
  }
}

/* ---------------------------------------------------------------------------
 * Chọn tỷ giá theo chính sách (CONSUMER THẬT của config — không no-op)
 * ------------------------------------------------------------------------- */

/** 1 ứng viên tỷ giá đọc từ `fx_rates` (đã lọc đúng cặp tiền + đúng nhóm). */
export interface FxRateCandidate {
  readonly id: string;
  readonly rateScaled: bigint;
  readonly effectiveAt: Date;
  readonly source: string;
}

export interface PickFxRateInput {
  readonly policy: FxRatePolicy;
  readonly candidates: readonly FxRateCandidate[];
  /** Thời điểm CHI (người dùng khai; vắng ⇒ caller truyền `now`). */
  readonly spentAt: Date;
  /** Thời điểm QUYẾT TOÁN (thường = now). */
  readonly now: Date;
  /** Cặp tiền (chỉ để thông điệp lỗi rõ ràng — candidates đã lọc đúng cặp ở tầng đọc). */
  readonly baseCurrency?: string;
  readonly quoteCurrency?: string;
}

/**
 * Chọn tỷ giá HIỆU LỰC theo chính sách: mốc thời gian = `spentAt` (at_spend) | `now` (at_settlement);
 * lấy hàng có `effectiveAt ≤ mốc` MỚI NHẤT. Không hàng nào ⇒ `FxRateMissingError` (422 fail-closed —
 * **KHÔNG lấy tỷ giá tương lai, KHÔNG bịa 1:1**).
 *
 * 🔴 Đây là consumer THẬT của `fx_rate_policy`: đổi policy ⇒ mốc đổi ⇒ tỷ giá chọn đổi ⇒ SỐ TIỀN VÀO
 * SỔ ĐỔI (không phải hằng số).
 */
export function pickFxRate(input: PickFxRateInput): FxRateCandidate {
  const cutoff = input.policy === "at_settlement" ? input.now : input.spentAt;
  const cutoffMs = cutoff.getTime();

  let best: FxRateCandidate | undefined;
  for (const c of input.candidates) {
    if (c.effectiveAt.getTime() > cutoffMs) continue; // tỷ giá CHƯA hiệu lực tại mốc — bỏ.
    if (!best || c.effectiveAt.getTime() > best.effectiveAt.getTime()) best = c;
  }
  if (!best) {
    throw new FxRateMissingError(
      input.baseCurrency ?? "?",
      input.quoteCurrency ?? "?",
      cutoff,
    );
  }
  return best;
}
